"use client";

import React from "react";
import { User, Calendar, Tag, CircleAlert, ArrowLeft } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AspectRatio } from "@/components/ui/aspect-ratio";


declare const wvcClient: any;

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div data-section-id="224032" className="animate-pulse space-y-8">
        <div className="space-y-4 text-center">
          <div className="h-12 w-3/4 mx-auto bg-muted rounded-sm" />
          <div className="h-4 w-1/2 mx-auto bg-muted rounded-sm" />
        </div>
        <div className="w-full h-[400px] bg-muted rounded-sm" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted rounded-sm" />
          <div className="h-4 w-full bg-muted rounded-sm" />
          <div className="h-4 w-5/6 bg-muted rounded-sm" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
        <CircleAlert className="size-12 text-destructive" />
        <h2 className="text-2xl font-default font-semibold text-foreground">Unable to load article</h2>
        <p className="text-muted-foreground">We encountered an issue retrieving this information. Please try again.</p>
        <Button onClick={refetch} variant="outline" className="rounded-sm border-primary text-primary">Refresh Page</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
        <h2 className="text-2xl font-default font-semibold text-foreground">Article Not Found</h2>
        <p className="text-muted-foreground">The resource you are looking for may have been moved or is no longer available.</p>
        <Link to="#">
          <Button variant="default" className="rounded-sm bg-primary text-primary-foreground">Return to Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="w-full max-w-4xl mx-auto">
      {}
      <header className="mb-10 text-center space-y-6">
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          {post.categories && post.categories.map((cat, i) => (
            <span key={cat.id} data-index={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-sm bg-accent text-accent-foreground text-xs font-medium uppercase tracking-wider">
              <Tag className="size-3" />
              {cat.name}
            </span>
          ))}
        </div>
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold leading-tight tracking-tight text-foreground">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground text-sm mt-6">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-8 border border-border">
                <AvatarImage src={post.author.avatar?.["48"]} alt={post.author.name} />
                <AvatarFallback className="bg-accent text-accent-foreground">{post.author.name.substring(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-1.5">
              <Calendar className="size-4" />
              <time dateTime={post.date}>{post.date}</time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 rounded-sm overflow-hidden shadow-sm border border-border/50">
          <AspectRatio ratio={16 / 9}>
            <img 
              src={post.featuredImage} 
              alt={post.title} 
              className="object-cover w-full h-full transition-transform duration-700 hover:scale-105"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div 
        className="prose prose-lg max-w-none font-serif leading-relaxed text-muted-foreground
                   prose-headings:text-foreground prose-headings:font-default prose-headings:font-bold
                   prose-a:text-primary prose-a:underline hover:prose-a:text-ring
                   prose-strong:text-foreground prose-strong:font-semibold
                   prose-blockquote:border-l-ring prose-blockquote:bg-accent/30 prose-blockquote:py-1 prose-blockquote:px-6 prose-blockquote:rounded-r-sm prose-blockquote:not-italic
                   prose-img:rounded-sm prose-img:shadow-sm"
        dangerouslySetInnerHTML={{ __html: post.content || "" }} 
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground mr-2">Topic Tags:</span>
          {post.tags.map((tag, i) => (
            <Link key={tag.id} to={tag.link || "#"} className="inline-block">
              <span data-index={i} className="px-3 py-1 bg-muted hover:bg-accent text-muted-foreground hover:text-accent-foreground text-sm rounded-sm transition-colors">
                #{tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12 bg-border/50" />

      {}
      {post.author && (
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 bg-card border border-border/50 p-8 rounded-sm shadow-sm">
          <Avatar className="size-24 border-2 border-background shadow-sm">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="text-2xl bg-accent text-accent-foreground">{post.author.name.substring(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-xl font-default font-bold text-foreground mb-2">
              Insights from {post.author.name}
            </h3>
            <p className="text-muted-foreground font-serif leading-relaxed">
              {post.author.slug === "admin" 
                ? "Jessica Gade, DPT, CSMT, is a doctor of physical therapy and certified canine sports massage therapist dedicated to canine mobility and comfort in Durango." 
                : "A professional bodywork expert sharing specialized guidance on mobility support and post-activity recovery for active and aging dogs."}
            </p>
            {post.author.link && (
              <div className="mt-4">
                <Link to={post.author.link}>
                  <Button variant="link" className="p-0 h-auto text-primary font-medium hover:text-ring">
                    More articles by {post.author.name} →
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {}
      <div className="mt-12 text-center">
        <Link to="#">
          <Button variant="outline" className="gap-2 rounded-sm px-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all">
            <ArrowLeft className="size-4" />
            Back to Resources
          </Button>
        </Link>
      </div>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && typeof wvcClient !== 'undefined' 
    ? wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", per_page: 1 }; 
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section 
      id="single-post-template-section" 
      className="bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 min-h-screen"
    >
      <div className="container mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}